import { useEffect, useRef } from "react";
import { getAllLandlords } from "../../../context/functions/AdminFunction.js";

export const useLandlordSearch = (
  filters,
  landlordSearchResults,
  setFilters,
  setLandlordSearchResults,
  setShowLandlordDropdown,
  setSearchingLandlords,
  abortControllerRef
) => {
  const searchRef = useRef(null);

  // Load initial landlords on mount
  useEffect(() => {
    const loadInitialLandlords = async () => {
      if (!filters.landlordId) {
        try {
          setSearchingLandlords(true);
          const res = await getAllLandlords(1);
          if (res && res.landlords) {
            setLandlordSearchResults(res.landlords.slice(0, 10));
            console.log("Loaded", res.landlords.slice(0, 10).length, "initial landlords");
          }
        } catch (err) {
          console.error("Error loading initial landlords:", err);
        } finally {
          setSearchingLandlords(false);
        }
      }
    };

    loadInitialLandlords();
  }, []); // Run once on mount

  // Handle search input focus
  const handleSearchFocus = () => {
    if (!filters.landlordId && landlordSearchResults.length > 0) {
      setShowLandlordDropdown(true);
    }
  };

  // Handle search input click
  const handleSearchClick = () => {
    if (!filters.landlordId && landlordSearchResults.length > 0) {
      setShowLandlordDropdown(true);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, landlordSearch: e.target.value }));
    if (filters.landlordId) {
      setFilters(prev => ({ ...prev, landlordId: "" }));
    }
  };

  // Landlord Search with debounce
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const delayDebounceFn = setTimeout(async () => {
      if (filters.landlordSearch.length >= 2 && !filters.landlordId) {
        setSearchingLandlords(true);
        try {
          const res = await getAllLandlords(1, filters.landlordSearch, undefined, undefined, undefined, undefined, undefined, controller.signal);
          if (res?.status === "canceled") {
            return;
          }
          if (res && res.landlords) {
            setLandlordSearchResults(res.landlords);
            setShowLandlordDropdown(true);
          }
        } catch (err) {
          console.error("Landlord search error:", err);
        } finally {
          setSearchingLandlords(false);
        }
      } else if (filters.landlordSearch.length === 0 && !filters.landlordId) {
        // When search is cleared, show initial results
      } else {
        setShowLandlordDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filters.landlordSearch, filters.landlordId, setLandlordSearchResults, setShowLandlordDropdown, setSearchingLandlords]);

  // Click outside dropdown listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowLandlordDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setShowLandlordDropdown]);

  // Handle landlord selection
  const handleSelectLandlord = (landlord) => {
    setFilters(prev => ({
      ...prev,
      landlordId: landlord._id,
      landlordSearch: `${landlord.fname_companyName} ${landlord.lname}`.trim() || landlord.email
    }));
    setShowLandlordDropdown(false);
  };

  // Clear landlord filter
  const clearLandlordFilter = () => {
    setFilters(prev => ({ ...prev, landlordId: "", landlordSearch: "" }));
  };

  return {
    searchRef,
    handleSearchFocus,
    handleSearchClick,
    handleSearchChange,
    handleSelectLandlord,
    clearLandlordFilter,
  };
};